// Shared / Player / Seek bar (Figma 4350:1902): Idle 4350:1889 · Dragging 4350:1894 · Read only 4350:1899.
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";
import { useTranslation } from "react-i18next";

export const SEEK_KEY_STEP_MS = 5_000;

/** "0:42", "2:03", "1:02:03". */
export function formatPlaybackTime(ms: number): string {
  const total = Math.max(0, Math.floor((Number.isFinite(ms) ? ms : 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

export interface PlayerSeekBarProps {
  positionMs: number;
  bufferedMs: number;
  durationMs: number;
  /** false = read only (cast viewer): played line only, no buffered, no thumb, no drag. */
  draggable: boolean;
  onSeek(ms: number): void;
  /** A drag started (true) / ended (false): the controls hold "seeking" meanwhile. */
  onDragChange?(dragging: boolean): void;
}

const pct = (v: number, d: number) => `${d > 0 ? Math.min(100, Math.max(0, (v / d) * 100)) : 0}%`;

/**
 * The seek bar and the "0:42 / 2:03" time under it. Track white 30 %, buffered white 50 %, played primary; thumb 12
 * (20 + shadow while dragging). A 48 px tall hit area around the 24 px row. Seeks once, on release.
 */
export default function PlayerSeekBar({ positionMs, bufferedMs, durationMs, draggable, onSeek, onDragChange }: PlayerSeekBarProps) {
  const { t } = useTranslation();
  const [dragMs, setDragMs] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const shownMs = dragMs ?? positionMs;
  const dragging = dragMs != null;
  // Unmounted mid-drag (a new item): release the "seeking" hold, or the next item's controls would never hide.
  const draggingRef = useRef(false);
  draggingRef.current = dragging;
  const onDragChangeRef = useRef(onDragChange);
  onDragChangeRef.current = onDragChange;
  useEffect(() => () => {
    if (draggingRef.current) onDragChangeRef.current?.(false);
  }, []);

  const msAt = (clientX: number) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || r.width <= 0 || durationMs <= 0) return 0;
    return Math.round(Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * durationMs);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // Primary button only (a right / middle press opens menus, it never drags).
    if (!draggable || durationMs <= 0 || e.button !== 0) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* jsdom / released pointer */ }
    setDragMs(msAt(e.clientX));
    onDragChange?.(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragging) setDragMs(msAt(e.clientX));
  };
  const end = (e: PointerEvent<HTMLDivElement>, commit: boolean) => {
    if (!dragging) return;
    const ms = msAt(e.clientX);
    setDragMs(null);
    onDragChange?.(false);
    if (commit) onSeek(ms);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!draggable || durationMs <= 0) return;
    const to =
      e.key === "ArrowRight" ? positionMs + SEEK_KEY_STEP_MS
      : e.key === "ArrowLeft" ? positionMs - SEEK_KEY_STEP_MS
      : e.key === "Home" ? 0
      : e.key === "End" ? durationMs
      : null;
    if (to == null) return;
    e.preventDefault();
    onSeek(Math.min(durationMs, Math.max(0, to)));
  };

  const trackH = !draggable ? "h-[3px]" : dragging ? "h-1.5" : "h-1";
  return (
    <div className="flex w-full flex-col gap-0.5">
      <div
        data-testid="player-controls-seek"
        {...(draggable
          ? {
              role: "slider",
              tabIndex: 0,
              "aria-label": t("player_seek", "Seek"),
              "aria-valuemin": 0,
              "aria-valuemax": Math.round(durationMs / 1000),
              "aria-valuenow": Math.round(shownMs / 1000),
              "aria-valuetext": `${formatPlaybackTime(shownMs)} / ${formatPlaybackTime(durationMs)}`,
            }
          : {
              role: "progressbar",
              "aria-label": t("player_seek", "Seek"),
              "aria-valuemin": 0,
              "aria-valuemax": Math.round(durationMs / 1000),
              "aria-valuenow": Math.round(shownMs / 1000),
            })}
        className={`relative flex h-12 -my-3 w-full items-center touch-none select-none ${draggable ? "cursor-pointer" : ""} focus-visible:outline-2 focus-visible:outline-white rounded`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => end(e, true)}
        onPointerCancel={(e) => end(e, false)}
        onKeyDown={onKeyDown}
      >
        <div ref={trackRef} className={`relative w-full overflow-visible rounded-full bg-white/30 ${trackH}`}>
          {draggable && (
            <div data-testid="player-controls-seek-buffered" className="absolute inset-y-0 left-0 rounded-full bg-white/50" style={{ width: pct(Math.max(bufferedMs, shownMs), durationMs) }} />
          )}
          <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--primary-fill)]" style={{ width: pct(shownMs, durationMs) }} />
          {draggable && (
            <div
              data-testid="player-controls-seek-thumb"
              className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--primary-fill)] ${dragging ? "size-5 shadow-[0_1px_4px_rgba(0,0,0,0.35)]" : "size-3"}`}
              style={{ left: pct(shownMs, durationMs) }}
            />
          )}
        </div>
      </div>
      <span className="text-xs leading-[1.3] text-white tabular-nums">
        {formatPlaybackTime(shownMs)} / {formatPlaybackTime(durationMs)}
      </span>
    </div>
  );
}
